import { IPublicCompany, industryImageMapping, industryMapping, getKeyByValue } from 'domain/entities/Company/Company';
import { MATCH_STATUSES } from 'domain/entities/Match/Match';
import { mqUser } from 'utils/responsive';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import NoImage from 'ui/images/no_image_case.png';
import dayjs from 'dayjs';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
interface CompanyCardProps {
    company: IPublicCompany;
}

const Wrapper = styled.div`
    position: relative;
    width: 31.5%;
    height: 400px;
    background-color: var(--white);
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 13px;
    min-width: 220px;
    min-height: 330px;
    cursor: pointer;
    border: 1px solid var(--main-color-blue);

    ${mqUser('SP')} {
        width: 100%;
        margin: 0 auto;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 20px;
`;

const Item = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const Title = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const Content = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const Industry = styled.div`
    font-size: var(--font-x-large);
    font-weight: var(--font-bold);
    color: var(--main-color-blue);
    margin: 5px 0;
`;

const ImageContainer = styled.div`
    position: relative;
    height: 170px;
`;

const Image = styled.img`
    border-radius: 13px 13px 0 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const Label = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: rgba(238, 121, 63, 0.8);
    color: white;
    padding: 5px 10px;
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    border-radius: 0 13px 0 0;
`;

const CompanyLabel = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    padding: 8px 10px;
    background-color: var(--main-color-blue);
    color: var(--white);
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
    border-radius: 13px 0 13px 0;
`;

const CreatedAt = styled.div`
    font-size: 12px;
    color: #222222;
    font-weight: 500;
`;

const BottomContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: auto;
    position: absolute;
    bottom: 15px;
    left: 20px;
    right: 20px;
`;

const getIndustryImage = (industry: string | null) => {
    if (!industry) {
        return NoImage;
    }
    const industryKey = getKeyByValue(industryMapping, industry);
    const industryImage = industryImageMapping[industryKey as keyof typeof industryImageMapping];
    return industryImage;
};

export const CompanyCard: React.FC<CompanyCardProps> = ({ company }) => {
    const navigate = useNavigate();
    const industryImage = getIndustryImage(company.industry);

    return (
        <Wrapper onClick={() => navigate(`/agent/companies/${company.id}/`)}>
            <ImageContainer>
                <Image src={industryImage} alt={company.industry} />
                {company?.match_status && (
                    <Label>
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_WAITING_ACCEPT && 'オファー済み'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_CONNECTED && 'マッチング済み'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_EXPIRED && 'オファー期限切れ'}
                        {company.match_status === MATCH_STATUSES.MATCH_STATUS_DECLINED && 'オファー辞退'}
                    </Label>
                )}
            </ImageContainer>
            <ContentWrapper>
                <Item>
                    <Industry>{company.industry}</Industry>
                    {company.label && <CompanyLabel>{company.label}</CompanyLabel>}
                </Item>
                {company.area && (
                    <Item>
                        <Title>エリア：</Title>
                        <Content>
                            {company.area_name}
                            {company.prefecture_name && ' / '}
                            {company.prefecture_name}
                        </Content>
                    </Item>
                )}
                {company.number_of_employees && (
                    <Item>
                        <Title>従業員規模：</Title>
                        <Content>{company.number_of_employees}</Content>
                    </Item>
                )}
                {company.revenue_scale && (
                    <Item>
                        <Title>売上高：</Title>
                        <Content>{company.revenue_scale}</Content>
                    </Item>
                )}
                {company.profit_level && (
                    <Item>
                        <Title>営業利益：</Title>
                        <Content>{company.profit_level}</Content>
                    </Item>
                )}
                {company.net_assets_level && (
                    <Item>
                        <Title>純資産金額：</Title>
                        <Content>{company.net_assets_level}</Content>
                    </Item>
                )}
                <BottomContainer>
                    <div>
                        {company.is_sole_proprietor && (
                            <ColorLabel color="#008000" backgroundColor="#d3ffa8">
                                個人事業主
                            </ColorLabel>
                        )}
                    </div>
                    <CreatedAt>最終更新日：{dayjs(company.updated_at).format('YYYY/MM/DD')}</CreatedAt>
                </BottomContainer>
            </ContentWrapper>
        </Wrapper>
    );
};
